/**
 * Zeichnungen für den Schritt „Bauausführung" — Ausstattungsstufen und die
 * Katalogmotive der Sonderausstattungen bzw. Wellness-Einrichtungen.
 *
 * Der Schritt war als einziger der Gebäudestrecke reine Schrift: ein
 * Auswahlfeld für die Bewertung und zwei Ankreuzlisten mit elf Zeilen. Die
 * Schritte davor (Bauartklasse, Dach, Stellung, Stellplätze) arbeiten mit
 * Bildern; hier bricht die Bildsprache mitten im Formular ab. Diese Datei
 * schließt die Lücke — Machart, viewBox und Farben sind aus
 * `bauartSchnitt.tsx` bzw. `StellplatzBlock.tsx` übernommen, nicht neu
 * erfunden: `0 0 100 80` mit Grundlinie bei y=74 für die Katalogmotive, Farben
 * ausschließlich aus `illustrationTokens.ts`, keine `<defs>`/`<pattern>` (also
 * keine `url(#…)`-IDs, die zwischen zwei Instanzen kollidieren könnten).
 *
 * **Was bewusst KEIN Bild bekommt.** Gezeichnet wird nur, was eine
 * unterscheidbare physische Ausprägung hat:
 *
 * - Der Denkmalschutz (`denkmalschutz`) bleibt eine Ja/Nein-Frage ohne Bild.
 *   Er ist ein Rechtsstatus, kein Baumerkmal: ein denkmalgeschütztes und ein
 *   ungeschütztes Haus sehen gleich aus. Jedes Symbol dafür wäre erfunden.
 * - Die Zusatzfelder der Kataloge (Wert, Einbaudatum, Anteil in Prozent, Lage)
 *   bleiben Eingabefelder. Ein Bild für „Wert (€)" trüge nichts bei.
 * - Der Freitext „Sonstige Sonderausstattung" bleibt Freitext.
 *
 * **Die Ausstattungsstufe zeigt zwei Merkmale, nicht drei.** „Gehoben",
 * „normal" und „minder" sind eine Gesamteinschätzung; gezeichnet werden kann
 * nur, woran man sie festmacht. Zwei Merkmale sind eindeutig und in jeder
 * Bewertungstabelle enthalten — das Fensterformat samt Verglasung und der
 * Bodenbelag. Ein drittes (etwa die Außenwand: Verblender / Putz / Putz) wäre
 * zwischen „normal" und „minder" nicht mehr wirklich verschieden gewesen und
 * hätte nur so ausgesehen. Lieber zwei eindeutige Merkmale als drei, von denen
 * eines Symbolik erfindet. Der gleichbleibende Wandausschnitt ist dabei
 * derselbe Kunstgriff wie der Sockel in `bauartSchnitt.tsx`: er ist der
 * konstante Rahmen, an dem die Unterschiede messbar werden.
 */
import { ILLU } from "./illustrationTokens";
import type {
  AusstattungsStufe,
  SONDERAUSSTATTUNGEN,
  WELLNESS_EINRICHTUNGEN,
} from "./enums";

/**
 * Motive der beiden Kataloge. Der Typ wird aus `enums.ts` abgeleitet, damit
 * `MOTIVE` unten vollständig sein MUSS: ein neuer Katalogeintrag ohne
 * Zeichnung ist dann ein Typfehler und keine leere Karte.
 */
export type AusstattungMotiv =
  | (typeof SONDERAUSSTATTUNGEN)[number]["key"]
  | (typeof WELLNESS_EINRICHTUNGEN)[number]["key"];

/* ------------------------------------------------- Ausstattungsstufe --- */

/**
 * Fensterformat und Verglasung je Stufe.
 *
 * `scheiben` wird als Zahl ineinanderliegender Rahmenlinien im Glasfeld
 * gezeichnet — 3 / 2 / 1. Das ist ein Zählmerkmal, keine Perspektive: in der
 * Ansicht sind die Scheiben eines Fensters nicht zu sehen. Abzählbar zu sein
 * ist hier wichtiger als perspektivisch richtig zu sein, denn ein Teil der
 * Nutzer sieht Farben eingeschränkt, und die Beschriftung neben der Zeichnung
 * nennt die Zahl ohnehin in Worten.
 *
 * `y + h` ist bei „gehoben" genau die Oberkante des Bodens (76) — das ist die
 * Aussage „bodentief" und deshalb kein Zufallswert.
 */
const FENSTER: Record<
  AusstattungsStufe,
  { x: number; y: number; w: number; h: number; scheiben: number; bank: boolean }
> = {
  gehoben: { x: 36, y: 18, w: 48, h: 58, scheiben: 3, bank: false },
  normal: { x: 40, y: 22, w: 40, h: 30, scheiben: 2, bank: true },
  minder: { x: 50, y: 26, w: 22, h: 18, scheiben: 1, bank: false },
};

/**
 * Parkett: drei durchlaufende Lagen mit nur vier versetzten Stoßfugen — lange
 * Stäbe. Regelmäßige Stoßfugen (alle 24) lasen sich in der Probe als
 * Mauerwerk; erst die spärlichen, unregelmäßig versetzten Stöße machen aus dem
 * Muster einen Bodenbelag. Die Gegenrichtung ist Absicht: Parkett zeigt vor
 * allem WAAGERECHTE Linien, die Fliesen darunter vor allem senkrechte.
 */
const PARKETT_FUGEN = "M10 81h100M10 86h100M44 76v5M82 81v5M30 86v6M86 86v6";

/** Fliesen/Laminat: gleichmäßiges Raster, fünf durchgehende Fugen. */
const FLIESEN_FUGEN = (() => {
  const teile: string[] = [];
  for (let x = 26; x <= 94; x += 17) teile.push(`M${x} 76v16`);
  return teile.join("");
})();

/**
 * Bodenaufbau je Stufe. Die Füllfarben sind nicht dekorativ gewählt: Parkett
 * ist Holz (`timberFill`), Fliesen sind Platten (`plateFill`), der fugenlose
 * Belag liegt auf dem Estrich (`sockelFill`). Sie unterscheiden sich zusätzlich
 * in der Helligkeit, und das Fugenbild trägt die Aussage auch ohne Farbe.
 */
const BODEN: Record<
  AusstattungsStufe,
  { fill: string; strich: string; fugen: string }
> = {
  gehoben: { fill: ILLU.timberFill, strich: ILLU.timberStroke, fugen: PARKETT_FUGEN },
  normal: { fill: ILLU.plateFill, strich: ILLU.wallStroke, fugen: FLIESEN_FUGEN },
  minder: { fill: ILLU.sockelFill, strich: ILLU.wallStroke, fugen: "" },
};

/** Der Wandausschnitt — bei allen drei Stufen identisch (siehe Dateikopf). */
const Wandzone = () => (
  <rect x={10} y={8} width={100} height={68} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
);

const Bodenzone = ({ stufe }: { stufe: AusstattungsStufe }) => {
  const boden = BODEN[stufe];
  return (
    <g>
      <rect x={10} y={76} width={100} height={16} fill={boden.fill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      {boden.fugen !== "" && (
        <path d={boden.fugen} stroke={boden.strich} strokeWidth={0.9} fill="none" />
      )}
    </g>
  );
};

const Fensterzone = ({ stufe }: { stufe: AusstattungsStufe }) => {
  const f = FENSTER[stufe];
  const scheiben = Array.from({ length: f.scheiben }, (_, i) => (i + 1) * 3);
  return (
    <g>
      {f.bank && (
        <rect
          x={f.x - 3}
          y={f.y + f.h}
          width={f.w + 6}
          height={3}
          fill={ILLU.sockelFill}
          stroke={ILLU.wallStroke}
          strokeWidth={1}
        />
      )}
      <rect x={f.x} y={f.y} width={f.w} height={f.h} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      {/* Die einzigen `fill="none"`-Rechtecke der Zeichnung — daran zählt der
          Test die Verglasung ab. */}
      {scheiben.map((abstand) => (
        <rect
          key={abstand}
          x={f.x + abstand}
          y={f.y + abstand}
          width={f.w - abstand * 2}
          height={f.h - abstand * 2}
          fill="none"
          stroke={ILLU.wallStroke}
          strokeWidth={0.9}
        />
      ))}
    </g>
  );
};

/**
 * Wortlaute je Stufe. `merkmale` sind die Beschriftungen **neben** der
 * Zeichnung — echter HTML-Text, damit die Aussage nicht am Bild allein hängt.
 * `lang` geht in `<title>`/`aria-label`.
 */
const STUFEN_WORTE: Record<
  AusstattungsStufe,
  { merkmale: readonly [string, string]; lang: string }
> = {
  gehoben: {
    merkmale: ["Bodentiefes Fenster, dreifach verglast", "Parkett im Stabverband"],
    lang: "Raumausschnitt mit bodentiefem, dreifach verglastem Fenster und Parkett im Stabverband",
  },
  normal: {
    merkmale: ["Fenster mit Brüstung, zweifach verglast", "Fliesen oder Laminat im Plattenraster"],
    lang: "Raumausschnitt mit Fenster über einer Brüstung, zweifach verglast, und Fliesen im Plattenraster",
  },
  minder: {
    merkmale: ["Kleines Fenster, einfach verglast", "Fugenloser einfacher Belag"],
    lang: "Raumausschnitt mit kleinem, einfach verglastem Fenster und fugenlosem einfachem Bodenbelag",
  },
};

/** Die zwei Kurzbeschriftungen neben der Zeichnung, von oben nach unten. */
export const stufenMerkmale = (stufe: AusstattungsStufe): readonly [string, string] =>
  STUFEN_WORTE[stufe].merkmale;

/** Wortlaut für `<title>`/`aria-label`. */
export const stufenBeschreibung = (stufe: AusstattungsStufe): string =>
  `${STUFEN_WORTE[stufe].lang}.`;

type StufenProps = {
  stufe: AusstattungsStufe;
  /** Maße kommen per CSS (Griffel); width/height sind nur der Fallback. */
  className?: string;
  /**
   * Nimmt die Zeichnung aus dem Barrierefreiheits-Baum — zu setzen, wo direkt
   * daneben schon Text steht, der dasselbe sagt. In der Karte des Pickers sind
   * das Titel, Hinweis und die beiden Merkmalszeilen; ohne `dekorativ` läge die
   * Beschreibung ein zweites Mal im Vorlesenamen der Radio-Karte. Dieselbe
   * Abwägung wie bei `BauartSchnitt`.
   */
  dekorativ?: boolean;
};

/**
 * Raumausschnitt einer Ausstattungsstufe: gleichbleibender Wandausschnitt,
 * darin das Fenster der Stufe, darunter ihr Bodenbelag.
 */
export const AusstattungsStufeBild = ({ stufe, className, dekorativ = false }: StufenProps) => {
  const beschreibung = stufenBeschreibung(stufe);
  return (
    <svg
      className={className}
      width="180"
      height="150"
      viewBox="0 0 120 100"
      fill="none"
      // Entweder Bild MIT Namen oder gar kein Eintrag im Baum — beides zugleich
      // wäre widersprüchlich.
      role={dekorativ ? undefined : "img"}
      aria-label={dekorativ ? undefined : beschreibung}
      aria-hidden={dekorativ ? true : undefined}
      focusable="false"
    >
      {/* Tooltip beim Hovern — und ohne `dekorativ` zugleich der Bildname. */}
      <title>{beschreibung}</title>
      <rect width="120" height="100" fill={ILLU.headerBg} />
      <Wandzone />
      <Bodenzone stufe={stufe} />
      <Fensterzone stufe={stufe} />
      <path d="M6 92h108" stroke={ILLU.wallStroke} strokeWidth={2.2} strokeLinecap="round" />
    </svg>
  );
};

/* ------------------------------------------------------ Katalogmotive --- */

/** Grundlinie aller Katalogmotive — dieselbe Höhe wie in `StellungPicker`. */
const Grundlinie = () => (
  <path d="M4 74h92" stroke={ILLU.roofStroke} strokeWidth={1.5} strokeLinecap="round" />
);

type MotivProps = { className?: string };

const svgProps = {
  width: "100",
  height: "80",
  viewBox: "0 0 100 80",
  fill: "none",
  "aria-hidden": true,
  focusable: "false",
} as const;

/**
 * Die elf Katalogmotive.
 *
 * Jedes zeigt das Bauteil selbst, nicht ein Sinnbild dafür — ein Aufzug ist
 * ein Schacht mit Kabine, Seil und Gegengewicht, keine Pfeilkombination. Die
 * Paare, die sich am ähnlichsten sind, sind bewusst am weitesten
 * auseinandergezogen: Personenaufzug (schmale Kabine am Seil) gegen
 * Lastenaufzug (breite Plattform mit Ladung), Schwimmbad (langes
 * Becken mit Leiter) gegen Whirlpool (kleine runde Wanne mit Düsen und Blasen),
 * Aufzug (senkrechter Schacht) gegen Rolltreppe (schräges Stufenband).
 */
const MOTIVE: Record<AusstattungMotiv, (p: MotivProps) => JSX.Element> = {
  /** Küchenzeile: Unterschränke mit Griffen, Arbeitsplatte, Oberschrank, Dunstabzug. */
  einbaukuechen: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <rect x={14} y={46} width={72} height={28} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d="M38 46v28M62 46v28" stroke={ILLU.jointStroke} strokeWidth={1} />
      <path d="M22 58h8M46 58h8M70 58h8" stroke={ILLU.jointStroke} strokeWidth={1.6} strokeLinecap="round" />
      <rect x={10} y={41} width={80} height={5} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      {/* Deckenlinie: ohne sie schweben Oberschrank und Haube im Bild. */}
      <path d="M8 10h84" stroke={ILLU.roofStroke} strokeWidth={1.5} strokeLinecap="round" />
      <rect x={14} y={10} width={30} height={18} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d="M29 10v18" stroke={ILLU.jointStroke} strokeWidth={1} />
      <rect x={68} y={10} width={8} height={8} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M56 30 64 18h16l8 12z" fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.4} strokeLinejoin="round" />
    </svg>
  ),
  /** Möblierung: Sofa mit zwei Kissen und Holzfüßen, daneben ein Beistelltisch. */
  moeblierteVermietung: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <rect x={16} y={32} width={44} height={16} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <rect x={12} y={40} width={8} height={22} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <rect x={56} y={40} width={8} height={22} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <rect x={20} y={48} width={36} height={14} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d="M38 48v14" stroke={ILLU.jointStroke} strokeWidth={1} />
      <path d="M16 62v8M60 62v8" stroke={ILLU.timberStroke} strokeWidth={2.5} strokeLinecap="round" />
      <rect x={72} y={50} width={24} height={4} fill={ILLU.timberFill} stroke={ILLU.timberStroke} strokeWidth={1.2} />
      <path d="M75 54v16M93 54v16" stroke={ILLU.timberStroke} strokeWidth={2} strokeLinecap="round" />
    </svg>
  ),
  /**
   * Rolltreppe: sechs steigende Stufen, dazu die beiden waagerechten
   * Landeplatten unten und oben und ein exakt parallel laufender Handlauf auf
   * drei Stützen. Ohne die Landeplatten las sich das Bild in der Probe als
   * gewöhnliche Treppe — sie sind das Merkmal, das eine Fahrtreppe ausmacht.
   */
  rolltreppen: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <path
        d="M18 74V68h11v-7h11v-7h11v-7h11v-7h11v-7h11V74z"
        fill={ILLU.wallFill}
        stroke={ILLU.wallStroke}
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
      <rect x={6} y={68} width={12} height={5} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <rect x={84} y={33} width={12} height={6} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M10 62 92 10" stroke={ILLU.roofStroke} strokeWidth={3} strokeLinecap="round" />
      <path d="M29 61v-11M51 47v-11M73 33v-11" stroke={ILLU.jointStroke} strokeWidth={1.4} />
    </svg>
  ),
  /** Trafo: stehender Kessel, seitliche Kühlrippen, drei Durchführungen oben. */
  trafos: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <rect x={18} y={38} width={8} height={28} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <rect x={74} y={38} width={8} height={28} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M21 38v28M78 38v28" stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <rect x={26} y={30} width={48} height={44} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <rect x={35} y={20} width={6} height={10} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <rect x={47} y={20} width={6} height={10} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <rect x={59} y={20} width={6} height={10} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M32 20h36" stroke={ILLU.wallStroke} strokeWidth={1.2} strokeLinecap="round" />
    </svg>
  ),
  /** Wallbox an der Wand: Gehäuse mit Blitz, Kabel und Stecker. */
  eLadestationen: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <rect x={6} y={8} width={16} height={66} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M6 26h16M6 44h16M6 62h16" stroke={ILLU.jointStroke} strokeWidth={0.9} />
      <rect x={24} y={20} width={26} height={36} rx={3} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path
        d="M39 26 31 40h6l-3 10 11-14h-6l3-10z"
        fill={ILLU.thatchFill}
        stroke={ILLU.thatchTexture}
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
      <path d="M50 46q18 4 20 14 2 10 12 10" stroke={ILLU.roofStroke} strokeWidth={2} fill="none" strokeLinecap="round" />
      <rect x={82} y={64} width={12} height={10} rx={2} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M86 64v-4M90 64v-4" stroke={ILLU.wallStroke} strokeWidth={1.2} strokeLinecap="round" />
    </svg>
  ),
  /** Personenaufzug im Schnitt: Schacht, Kabine am Seil, Gegengewicht über zwei Rollen. */
  personenaufzug: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <rect x={24} y={8} width={52} height={66} fill={ILLU.headerBg} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d="M42 10h20" stroke={ILLU.roofStroke} strokeWidth={1.2} />
      <circle cx={42} cy={14} r={4} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <circle cx={62} cy={14} r={4} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M42 18v16M62 18v26" stroke={ILLU.roofStroke} strokeWidth={1.2} />
      <rect x={28} y={34} width={28} height={30} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d="M42 34v30" stroke={ILLU.jointStroke} strokeWidth={1} />
      <rect x={57} y={44} width={10} height={18} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M24 66h-6M76 66h6" stroke={ILLU.jointStroke} strokeWidth={1.4} strokeLinecap="round" />
    </svg>
  ),
  /** Lastenaufzug: breiter Schacht, offene Plattform mit Palette und Kisten. */
  lastenaufzug: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <rect x={12} y={8} width={76} height={66} fill={ILLU.headerBg} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <circle cx={30} cy={14} r={4} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <circle cx={70} cy={14} r={4} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M30 18v22M70 18v22" stroke={ILLU.roofStroke} strokeWidth={1.2} />
      <rect x={18} y={60} width={64} height={5} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.4} />
      <path d="M18 40h64M18 40v20M82 40v20" stroke={ILLU.jointStroke} strokeWidth={1.2} />
      <rect x={26} y={56} width={30} height={4} fill={ILLU.timberStroke} stroke={ILLU.wallStroke} strokeWidth={1} />
      <rect x={28} y={44} width={12} height={12} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <rect x={42} y={46} width={12} height={10} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M12 66h-6M88 66h6" stroke={ILLU.jointStroke} strokeWidth={1.4} strokeLinecap="round" />
    </svg>
  ),

  /** Balkone: zwei auskragende Platten mit je fünf Geländerstäben an der Fassade. */
  balkone: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <rect x={8} y={8} width={26} height={66} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d="M8 26h26M8 44h26M8 62h26" stroke={ILLU.jointStroke} strokeWidth={0.9} />
      <rect x={34} y={30} width={54} height={5} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M36 18h52" stroke={ILLU.roofStroke} strokeWidth={2} strokeLinecap="round" />
      <path d="M42 18v12M52 18v12M62 18v12M72 18v12M82 18v12" stroke={ILLU.jointStroke} strokeWidth={1.2} />
      <rect x={34} y={62} width={54} height={5} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M36 50h52" stroke={ILLU.roofStroke} strokeWidth={2} strokeLinecap="round" />
      <path d="M42 50v12M52 50v12M62 50v12M72 50v12M82 50v12" stroke={ILLU.jointStroke} strokeWidth={1.2} />
    </svg>
  ),
  /** Schwimmbad im Schnitt: langes Becken mit Wasserspiegel, Wellen und Einstiegsleiter. */
  schwimmbad: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <rect x={4} y={38} width={12} height={6} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <rect x={84} y={38} width={12} height={6} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <rect x={16} y={44} width={68} height={26} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d="M20 52q6-3 12 0t12 0 12 0 12 0 12 0" stroke={ILLU.wallStroke} strokeWidth={1.2} fill="none" />
      <path d="M22 30v14M32 30v14M22 34h10M22 39h10" stroke={ILLU.roofStroke} strokeWidth={1.8} strokeLinecap="round" />
    </svg>
  ),
  /** Whirlpool: kleine runde Wanne mit drei Düsen und aufsteigenden Blasen. */
  whirlpool: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <path
        d="M30 44v14a20 14 0 0 0 40 0V44z"
        fill={ILLU.glassFill}
        stroke={ILLU.wallStroke}
        strokeWidth={1.5}
      />
      <rect x={26} y={40} width={48} height={5} rx={2} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <circle cx={35} cy={54} r={2.6} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <circle cx={65} cy={54} r={2.6} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <circle cx={50} cy={64} r={2.6} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <circle cx={39} cy={32} r={3.4} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1} />
      <circle cx={48} cy={22} r={2.6} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1} />
      <circle cx={58} cy={30} r={3} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1} />
      <circle cx={65} cy={20} r={2.2} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1} />
    </svg>
  ),
  /** Sauna: Holzkabine mit zwei Bänken und Ofen mit drei Steinen. */
  sauna: ({ className }) => (
    <svg className={className} {...svgProps}>
      <Grundlinie />
      <rect x={14} y={16} width={72} height={58} fill={ILLU.timberFill} stroke={ILLU.timberStroke} strokeWidth={1.5} />
      <path d="M14 26h72M14 36h72M14 46h72M14 56h72M14 66h72" stroke={ILLU.timberStroke} strokeWidth={0.9} />
      <rect x={20} y={40} width={30} height={5} fill={ILLU.timberFill} stroke={ILLU.timberStroke} strokeWidth={1.2} />
      <rect x={20} y={56} width={30} height={5} fill={ILLU.timberFill} stroke={ILLU.timberStroke} strokeWidth={1.2} />
      <rect x={60} y={48} width={18} height={22} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <circle cx={64} cy={46} r={3} fill={ILLU.roofFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <circle cx={70} cy={45} r={3} fill={ILLU.roofFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <circle cx={76} cy={46} r={3} fill={ILLU.roofFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <path d="M63 38q3-4 6 0t6 0" stroke={ILLU.wallStroke} strokeWidth={1.1} fill="none" />
    </svg>
  ),
};

/**
 * Ein Katalogmotiv. Rein dekorativ (`aria-hidden` im SVG selbst): die
 * Beschriftung der Karte steht direkt daneben und sagt dasselbe.
 */
export const AusstattungIcon = ({
  motiv,
  className,
}: {
  motiv: AusstattungMotiv;
  className?: string;
}) => {
  const Motiv = MOTIVE[motiv];
  return <Motiv className={className} />;
};

/** Nur für Tests: die Liste aller gezeichneten Motive. */
export const ALLE_MOTIVE = Object.keys(MOTIVE) as AusstattungMotiv[];
